import {
  ensureContrast,
  getDefaultCSSColor,
  getLightenedColor,
  getShadedColor,
  getTagBorderColor,
  getTagFillColor,
} from '@ardoq/color-helpers';
import { ArdoqIconCategory, getIcon, IconName } from '@ardoq/icons';
import {
  ComponentRepresentationData,
  ComponentRepresentationDataWithColorShades,
  ComponentTypeIconData,
} from './types';

export const getComponentTypeRepresentationDataFromComponentType = (
  componentType: ComponentTypeIconData
): ComponentRepresentationData | null => {
  const color =
    (componentType &&
      (componentType.color || getDefaultCSSColor(componentType.level))) ||
    'black';

  if (componentType.image) {
    return {
      icon: {
        // graphics.icon.none. Avoiding the import to not get cyclic dependency
        id: 'none',
        label: 'none',
        category: ArdoqIconCategory.Ardoq,
      },
      isImage: true,
      value: componentType.image,
      color,
    };
  }
  if (componentType.icon) {
    return {
      icon: getIcon(componentType.icon),
      isImage: false,
      value: componentType.icon,
      color,
    };
  }
  return null;
};

const DEFAULT_COMPONENT_REPRESENTATION_DATA = {
  isImage: false,
  value: '',
  icon: {
    category: ArdoqIconCategory.FontAwesome,
    id: IconName.SHOW_COMPONENT,
    label: 'Component Type',
  },
  color: 'black',
};

export const getComponentRepresentationDataWithColorShades = ({
  color,
  level,
  image,
  icon,
}: ComponentTypeIconData): ComponentRepresentationDataWithColorShades => {
  const lightenedColor = getLightenedColor(color ?? 'black');
  const contrastColor = ensureContrast(
    lightenedColor,
    getShadedColor(color ?? 'black')
  );
  /**
   *  If a color is "null", it defaults to "white",
   *  so we should use the contrastColor to maintain contrast
   */
  const shadedColor = color ? getShadedColor(color) : contrastColor;

  const componentRepresentationData =
    getComponentTypeRepresentationDataFromComponentType({
      color,
      level,
      image,
      icon,
    }) ?? DEFAULT_COMPONENT_REPRESENTATION_DATA;

  return {
    isImage: componentRepresentationData.isImage,
    value: componentRepresentationData.value,
    icon: componentRepresentationData.icon,
    color: color ?? 'black',
    lightenedColor,
    shadedColor,
    contrastColor,
  };
};

/**
 * Color shades for a component rendered as a tag/chip: a tinted fill, a tinted
 * border, and a label/icon color that keeps contrast against the fill. Use this
 * over `getComponentRepresentationDataWithColorShades` whenever the component is
 * shown as a tag (e.g. ComponentDecoratorAsTag).
 * Current usage: Viewpoint Builder and Data Inventory - Reference columns
 */
export const getComponentRepresentationDataWithTagColors = ({
  color,
  level,
  image,
  icon,
}: ComponentTypeIconData): ComponentRepresentationDataWithColorShades => {
  const componentRepresentationData =
    getComponentTypeRepresentationDataFromComponentType({
      color,
      level,
      image,
      icon,
    }) ?? DEFAULT_COMPONENT_REPRESENTATION_DATA;

  const tagFillColor = getTagFillColor(componentRepresentationData.color);
  const tagBorderColor = getTagBorderColor(componentRepresentationData.color);
  const contrastColor = ensureContrast(
    tagFillColor,
    componentRepresentationData.color
  );

  return {
    isImage: componentRepresentationData.isImage,
    value: componentRepresentationData.value,
    icon: componentRepresentationData.icon,
    color: componentRepresentationData.color,
    lightenedColor: tagFillColor,
    shadedColor: tagBorderColor,
    contrastColor,
  };
};
